import React, {Fragment} from 'react';
import {Event} from 'types/Event';
import {Menu, Transition} from "@headlessui/react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../context/AuthContext";
import {ClockIcon, EyeOffIcon, ThumbUpIcon} from "@heroicons/react/outline";
import FeedbackBadge from "../FeedbackBadge";
import {DropdownItem} from "../Dropdown";
import {EyeIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import {formattedTime, isSameTimezone} from "../../services/date";
import {useOperator} from "../../context/OperatorContext";

// Types ----

type EventPreviewProps = {
  event: Event
  onDeleteEvent: (event: Event) => void
  viewDisabled?: boolean
  editDisabled?: boolean
  deleteDisabled?: boolean
}

// Component ----

function EventPreview({event, onDeleteEvent, viewDisabled, editDisabled, deleteDisabled}: EventPreviewProps) {
  const navigate = useNavigate();
  const auth = useAuth();
  const {settings} = useOperator();

  function viewEvent() {
    void navigate(`/events/${event.id}`)
  }

  function editEvent() {
    void navigate(`/events/${event.id}/edit`)
  }

  function deleteEvent() {
    onDeleteEvent(event);
  }

  const sameTimezone = isSameTimezone(event?.timezone);

  return (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items className='origin-top mt-2 absolute z-30 focus:outline-none'>
        <div className="py-1 w-72 rounded-md shadow-lg bg-white">
          {/* Header */}
          <div className='px-4 py-3 border-b border-b-gray-100 text-sm mb-1'>
            <div className='flex items-center gap-2'>
              {!event.is_published && <EyeOffIcon className="h-3.5 w-3.5 text-gray-800"/>}
              <p className="font-semibold text-gray-900 line-clamp-4">{event.name}</p>
            </div>
            <p className="text-gray-400 mt-1.5">{event.type.name}</p>

            {/* Date/Time */}
            {(sameTimezone || event.is_all_day) ?
              <time dateTime={event.starts_at.toISO()} className="mt-2.5 flex items-center text-gray-700">
                <ClockIcon className="mr-2 h-4 w-4 text-gray-400" aria-hidden="true"/>
                {event.is_all_day ? 'All day' : formattedTime(event.starts_at, false, undefined, settings?.localisation.locale )}
              </time> :
              <>
                <time dateTime={event.starts_at.setZone(event.timezone).toISO()} className="mt-2.5 flex items-center text-gray-700">
                  <ClockIcon className="mr-2 h-4 w-4 text-gray-400" aria-hidden="true"/>
                  {event.is_all_day ? 'All day' : formattedTime(event.starts_at, true, event.timezone, settings?.localisation.locale)}
                </time>
                <time dateTime={event.starts_at.toISO()} className="mt-2.5 flex items-center text-gray-700">
                  <ClockIcon className="mr-2 h-4 w-4 text-gray-400" aria-hidden="true"/>
                  {event.is_all_day ? 'All day' : formattedTime(event.starts_at, true, undefined, settings?.localisation.locale)}
                </time>
              </>
            }

            {/* Feedback */}
            <div className="mt-2.5 flex items-center">
              <ThumbUpIcon className="mr-2 h-4 w-4 text-gray-400" aria-hidden="true"/>
              <FeedbackBadge
                count={event.aggregated_feedback?.count ?? 0}
                positiveRate={event.aggregated_feedback?.positive_rate ?? 0}
              />
            </div>
          </div>

          {auth.can('events:read') &&
            <DropdownItem name="View" icon={EyeIcon} onClick={viewEvent} disabled={!!viewDisabled}/>
          }

          {auth.can('events:write') &&
            <DropdownItem name="Edit" icon={PencilIcon} onClick={editEvent} disabled={!!editDisabled}/>
          }

          {auth.can('events:write') &&
            <DropdownItem name="Delete" icon={TrashIcon} onClick={deleteEvent} disabled={!!deleteDisabled}/>
          }
        </div>
      </Menu.Items>
    </Transition>
);
}

export default EventPreview;